import { cloneElement, isValidElement, type ComponentProps, type ReactNode } from "react";

import { cn } from "@/lib/utils";

import { Button } from "./button";
import { MUI_BUTTON_BASE_RESET } from "./button-variants";
import { CircularProgress } from "./circular-progress";

// MUI's `Button` under the donor theme — `@modsy/react-ui`'s `Button`, which
// the specifications screens compose in 18 files and the catalog in 7
// (`docs/migration/research/timmy-spec-ui-inventory.md` §3).
//
// Before this, the catalog had class constants rather than a component —
// three `MUI_BUTTON_*` shapes in `mui-field-shape.ts`, retired once every
// caller had moved here (#657) — and every caller
// re-assembled the rest on a raw `Button` or `<a>`: the anchor's flex
// centring, the width, which shadcn variant to start from. The small size had
// no constant at all, so the batch toolbar and the pager each hand-built one,
// and drifted (an `oklab` alpha against an `rgba` one, a pinned height against
// MUI's padding, a ripple in one and not the other).
//
// **What rides on `ui/button.tsx`**, unchanged: the press ripple (MUI's
// `ButtonBase`, `focusRipple` included), `disableRipple`, `asChild`, and MUI's
// `loading` — the start-pinned `CircularProgress` and the spacer that holds the
// label still. This file adds only MUI's shape.
//
// **The shape is MUI 9's own** (`Button.mjs`), because the donor theme's
// `MuiButton` override touches only `fontWeight: 500`, `textTransform: 'none'`
// and `whiteSpace: 'nowrap'`, plus its disabled rule (below):
//
//   - `typography.button` — 500 over a 1.75 line, 13/14/15px by size — in 4px
//     corners, over a 64px floor, the padding carrying the height (hence no
//     `h-*`: MUI has none),
//   - three variants, each with its own padding table, where an outlined
//     button's 1px border eats a pixel of each side,
//   - four named properties on the `short` clock (250ms) rather than
//     `transition-all`: MUI never transitions `opacity`, so a button MUI snaps
//     to its disabled ink does not fade to it here either.
//
// **Disabled is the theme's, not MUI's.** `MuiButton.root` sets `opacity: 0.5`
// and puts each variant's own ink back, so a disabled button is itself at half
// strength rather than MUI's grey chip; a contained one keeps its ground. The
// outlined border is the one thing left at MUI's `action.disabledBackground`.
//
// **Focus is the app's ring** (D12, app-wide), in place of the pulsating focus
// ripple this app ruled out (`touch-ripple.tsx`). It also replaces shadcn's
// base, whose 3px halo recoloured an outlined button's border on focus; MUI's
// border never moves on focus.
//
// Every alpha and shadow is an `index.css` token holding MUI's literal `rgba`
// (`lennar-primary-outline`, `shadow-mui-elevation-*`), never a Tailwind `/NN`
// modifier: those compile to `color-mix(in oklab, …)`, a different colour from
// the one the donor states and unreadable beside the number it came from.

/**
 * What every variant shares: MUI's root, and the shadcn base's opinions
 * turned back off. `ui/button.tsx` is handed no shadcn `variant` or `size`,
 * so only its base classes arrive, and `cn` resolves each of these over them.
 */
const MUI_BUTTON_ROOT = cn(
  // `ButtonBase`'s reset — the hand, no drag-select on the label, no tap
  // flash, the plain arrow once disabled — which the shadcn base carries only
  // the first of.
  MUI_BUTTON_BASE_RESET,
  // `shrink` puts back MUI's `flex-shrink: 1` over the shadcn base's
  // `shrink-0`: MUI's root sets none, so a donor button in a row too narrow
  // for it gives way — the pager's forward pair, measured at 375px.
  "min-w-16 shrink rounded-[4px] font-medium whitespace-nowrap normal-case",
  // Chrome's `button { letter-spacing: normal }`, which the donor's button
  // keeps — MUI's button variant names no tracking under the donor's Inter
  // theme (`createTypography.js:56-58`) — and Tailwind's preflight turns into
  // `inherit`. Inherited, a table frame's `-0.01em` narrowed a row's Unlink by
  // a pixel (#1085).
  "tracking-normal",
  // MUI's root sets no `gap`: an icon rides in a start/end slot with its own
  // margins, so the shadcn base's `gap-2` would stand on top of them.
  "gap-0",
  "transition-[background-color,box-shadow,border-color,color] duration-[250ms]",
  // `outline-solid` said out loud: the shadcn base's `outline-none` sets
  // `--tw-outline-style: none`, which `outline-2` would otherwise inherit.
  "focus-visible:outline-lennar-blue-600 focus-visible:ring-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid",
);

/**
 * Padding and type by variant and size, as MUI's table has them. Literal
 * strings, because Tailwind v4 scans source text and generates nothing for a
 * class assembled by interpolation.
 *
 * The 1.75 line rides **after** each size's `text-*`: a Tailwind v4 font-size
 * utility sets a line height too, so `tailwind-merge` drops any `leading-*`
 * that precedes one — which put every button on its size's default line,
 * 3–4px short of MUI's.
 */
const SIZE_CLASSES = {
  text: {
    small: "px-[5px] py-1 text-[0.8125rem] leading-[1.75]",
    medium: "px-2 py-1.5 text-[0.875rem] leading-[1.75]",
    large: "px-[11px] py-2 text-[0.9375rem] leading-[1.75]",
  },
  outlined: {
    small: "px-[9px] py-[3px] text-[0.8125rem] leading-[1.75]",
    medium: "px-[15px] py-[5px] text-[0.875rem] leading-[1.75]",
    large: "px-[21px] py-[7px] text-[0.9375rem] leading-[1.75]",
  },
  contained: {
    small: "px-[10px] py-1 text-[0.8125rem] leading-[1.75]",
    medium: "px-4 py-1.5 text-[0.875rem] leading-[1.75]",
    large: "px-[22px] py-2 text-[0.9375rem] leading-[1.75]",
  },
} as const;

/** The primary colour's inks, per variant. */
const VARIANT_CLASSES = {
  /** `primary.main` ink, no ground until a 4% hover. */
  text: "text-lennar-blue-600 bg-transparent shadow-none hover:bg-lennar-primary-hover",
  /**
   * `primary.main` ink in a border of that colour at half alpha, which goes
   * solid on hover over the same 4% ground.
   */
  outlined: cn(
    "text-lennar-blue-600 border border-lennar-primary-outline bg-transparent shadow-none",
    "hover:border-lennar-blue-600 hover:bg-lennar-primary-hover",
    // Undoing shadcn's `focus-visible:border-ring` without outranking the
    // hover: MUI's hover sets the solid border whatever the focus
    // (`Button.mjs:162-172`), so a focused button still firms up under the
    // pointer.
    "focus-visible:border-lennar-primary-outline focus-visible:hover:border-lennar-blue-600",
    "disabled:border-lennar-action-disabled-bg",
  ),
  /**
   * `#003c77` under white at elevation 2; `primary.dark` (which the donor
   * theme sets rather than leaving to `darken`) at elevation 4 on hover, 6 on
   * keyboard focus, 8 while pressed — and no shadow once disabled, with the
   * ground kept.
   */
  contained: cn(
    "bg-lennar-blue-600 text-white",
    "shadow-mui-elevation-2",
    "hover:bg-lennar-blue-hover hover:shadow-mui-elevation-4",
    "focus-visible:shadow-mui-elevation-6",
    "active:shadow-mui-menu",
    // MUI emits `&:active` before `&.Mui-focusVisible` at equal specificity
    // (`Button.mjs:120-125`), so a focused button held down stays at 6.
    "focus-visible:active:shadow-mui-elevation-6",
    "disabled:bg-lennar-blue-600 disabled:text-white disabled:shadow-none",
  ),
} as const;

/**
 * MUI's `color="error"`, outlined — the one other colour a ported surface asks
 * for (Core Spec Links' Unlink, #1085; `CoreSpecLinks.tsx:272-276`). The same
 * three alphas as the primary's outline, over `error.main` (`red.A400`, the
 * donor theme never overrode it): the ink, a border at half alpha going solid on
 * hover, and the 4% hover ground (`Button.mjs`, the `color` variants).
 */
const OUTLINED_ERROR_CLASSES = cn(
  "text-lennar-error border-lennar-error-outline hover:border-lennar-error hover:bg-lennar-error-hover",
  "focus-visible:border-lennar-error-outline focus-visible:hover:border-lennar-error",
);

/**
 * MUI's `startIcon` / `endIcon` slots (`Button.js:296-384`), by size.
 *
 * `display: inherit` — the root's `inline-flex` — with the start slot 8px
 * before the label and pulled 4px into the padding, 2px at `small`, and the
 * end slot the mirror image. The first thing inside either is resized to the
 * size's icon: `& > *:nth-of-type(1) { fontSize: 18 | 20 | 22 }`
 * (`commonIconStyles`, `:54-83`), which on an `SvgIcon`'s `1em` box is its
 * width and height. The `>` child selector outranks a glyph's own `size-*`
 * class, as MUI's slot rule outranks `SvgIcon`'s default.
 *
 * MUI also gives the start slot a zero-width `::before` so it holds a line box;
 * inside the root's centred row that moves nothing, so it is not carried.
 */
const ICON_SIZE_CLASSES = {
  small: "[&>*:nth-of-type(1)]:size-[18px]",
  medium: "[&>*:nth-of-type(1)]:size-5",
  large: "[&>*:nth-of-type(1)]:size-[22px]",
} as const;

const START_ICON_CLASSES = {
  small: "mr-2 -ml-0.5",
  medium: "mr-2 -ml-1",
  large: "mr-2 -ml-1",
} as const;

const END_ICON_CLASSES = {
  small: "ml-2 -mr-0.5",
  medium: "ml-2 -mr-1",
  large: "ml-2 -mr-1",
} as const;

/**
 * Where a start-position loading spinner pins (`Button.js:390-424`): 14px in,
 * 10 at `small`, and 6 for a `text` button, whose rule MUI lists last.
 */
function loadingLeft(variant: MuiButtonVariant, size: MuiButtonSize) {
  if (variant === "text") return "left-1.5";
  if (size === "small") return "left-2.5";
  return "left-3.5";
}

export type MuiButtonVariant = keyof typeof SIZE_CLASSES;
export type MuiButtonSize = keyof (typeof SIZE_CLASSES)["text"];

export interface MuiButtonProps extends Omit<ComponentProps<typeof Button>, "variant" | "size"> {
  /** MUI's `variant`; `text` when omitted, as MUI defaults it. */
  variant?: MuiButtonVariant;
  /** MUI's `size`; `medium` when omitted. */
  size?: MuiButtonSize;
  /** `width: 100%`. */
  fullWidth?: boolean;
  /** MUI's `color`; `primary` when omitted. `error` is drawn for `outlined`. */
  color?: "primary" | "error";
  /**
   * MUI's `startIcon`. With `loading`, the icon fades out in its own slot and
   * the spinner takes its place — react-ui's `loadingPosition='start'`
   * (`button.tsx:22-24`) — so the label never moves.
   */
  startIcon?: ReactNode;
  /** MUI's `endIcon` — inside the anchor too, under `asChild`. */
  endIcon?: ReactNode;
}

export function MuiButton({
  variant = "text",
  size = "medium",
  fullWidth = false,
  color = "primary",
  startIcon,
  endIcon,
  loading = false,
  disabled,
  asChild = false,
  className,
  children,
  ...props
}: MuiButtonProps) {
  // MUI synthesises a start-icon slot for a loading button that has none —
  // `startIcon = startIconProp || loading && loadingPosition === 'start'`,
  // holding an empty `1em` placeholder at the size's icon font
  // (`Button.js:537-543`). `ui/button.tsx`'s own spacer is that slot at
  // **large** only (its note: every loading caller there is MUI-large), so a
  // medium or small loading button — the target-cost card's Upload (#1213) —
  // gets MUI's own placeholder here instead, sized by the slot's classes.
  const placeholder =
    loading && !asChild && startIcon === undefined && size !== "large" ? (
      <span data-slot="mui-button-loading-placeholder" className="inline-block" />
    ) : undefined;
  const startSlot = startIcon ?? placeholder;
  // A start icon is MUI's loading placeholder, so this file runs the load:
  // `ui/button.tsx`'s own `loading` draws a spacer of its own beside the
  // label, which is MUI's arrangement only for a button *without* one
  // (`Button.js:552-556`).
  const iconLoading = loading && !asChild && startSlot !== undefined;
  const start =
    startSlot === undefined ? null : (
      <span
        key="start"
        data-slot="mui-button-start-icon"
        className={cn(
          "inline-flex items-center",
          START_ICON_CLASSES[size],
          ICON_SIZE_CLASSES[size],
          // `getTransitionStyles(theme, ['opacity'], { duration: short })`,
          // declared inside the loading variant only (`Button.js:318-328`): it
          // fades out for a load and is simply back after one.
          iconLoading && "opacity-0 transition-opacity duration-[250ms]",
          // Full width, the faded slot pulls its right margin to −8px
          // (`:330-337`), so the in-row spinner and the label sit together.
          iconLoading && fullWidth && "mr-[-8px]",
        )}
      >
        {startSlot}
      </span>
    );
  const end =
    endIcon === undefined ? null : (
      <span
        key="end"
        data-slot="mui-button-end-icon"
        className={cn("inline-flex", END_ICON_CLASSES[size], ICON_SIZE_CLASSES[size])}
      >
        {endIcon}
      </span>
    );
  const content =
    start === null && end === null ? (
      children
    ) : asChild ? (
      // `Slot` renders the one child element, so the slots go *inside* it,
      // around its own children — an anchor's icon is the anchor's. Still one
      // element, so `Button` can put its ripple layer inside it the same way.
      isValidElement<{ children?: ReactNode }>(children) ? (
        cloneElement(children, undefined, start, children.props.children, end)
      ) : (
        children
      )
    ) : (
      <>
        {/* MUI's order: the start icon, the loading wrapper, the label
            (`Button.js:575`). Over the button the spinner's place is moot;
            full width it is in the row, just after the faded icon. */}
        {start}
        {/* No `aria-hidden`: the donor's indicator is a bare
            `CircularProgress`, announced (`Button.js:479`). */}
        {iconLoading ? (
          <CircularProgress
            size={20}
            color="inherit"
            className={cn(
              // Full width, MUI puts the spinner in the row rather than over
              // it — `position: relative; left: -10px` (`Button.js:455-462`).
              fullWidth ? "relative -left-2.5" : cn("absolute", loadingLeft(variant, size)),
            )}
          />
        ) : null}
        {children}
        {end}
      </>
    );
  return (
    <Button
      {...props}
      asChild={asChild}
      loading={iconLoading ? false : loading}
      disabled={disabled || iconLoading}
      // `null`, not omitted: `buttonVariants` then emits no shadcn variant or
      // size classes at all, so there is no `h-9` or `hover:bg-primary/90` for
      // the classes below to have to out-order.
      variant={null}
      size={null}
      className={cn(
        MUI_BUTTON_ROOT,
        SIZE_CLASSES[variant][size],
        VARIANT_CLASSES[variant],
        color === "error" && variant === "outlined" && OUTLINED_ERROR_CLASSES,
        fullWidth && "w-full",
        iconLoading && "relative",
        className,
      )}
    >
      {content}
    </Button>
  );
}
